import { useEffect, useState } from 'react'
import { UploadFile } from '../../shared/upload-file/upload-file'
import { Box, Pagination, useTheme } from '@mui/material'
import { Button } from '../../shared/button/button'
import { formatDate, validateExcelFile } from '../../shared/utils'
import {
  createExcelMassivePayment,
  getExcelMassivePaymentsStatus,
  validateExcelMassivePayment,
} from '../../redux/actions/transactions.actions'
import { useDispatch, useSelector } from 'react-redux'
import { Announcement } from '../../shared/announcement/announcement'
import { NormalText } from '../../shared/styles'
import {
  setMassivePaymentExcelValidation,
  setMassivePaymentExcelPayment,
} from '../../redux/reducers/transactions.reducer'
import { mockedTokensIcons } from '../mockedData'
import { Loader } from '../../shared/loader/loader'
import { getTagType, StatesMassivePayments } from './multiple-payments.utils'
import { Tag } from '../../shared/tag/tag'
import { useGetUser } from '../../hooks/useGetUser'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export const CreateTransactionExcel = () => {
  const [inputFile, setInputFile] = useState(null)
  const [errorUploadFile, setErrorUploadFile] = useState('')
  const [showDetails, setShowDetails] = useState(false)
  const [showDetailsMassivePayments, setShowDetailsMassivePayments] =
    useState(false)

  const { user } = useGetUser()
  const { palette } = useTheme()

  const {
    validation: {
      data: dataMassivePaymentsExcelValidation,
      isLoading: isLoadingMassivePaymentsExcelValidation,
      errors: errorsMassivePaymentsExcelValidation,
    },
    payment: {
      data: dataMassivePaymentsExcelPayment,
      isLoading: isLoadingMassivePaymentsExcelPayment,
      error: errorMassivePaymentsExcelPayment,
    },
    status: {
      data: dataMassivePaymentsExcelStatus,
      isLoading: isLoadingMassivePaymentsExcelStatus,
      error: errorMassivePaymentsExcelStatus,
    },
  } = useSelector((state) => state.transactions.massivePayments.excel)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  function handleAddFile(e) {
    e.preventDefault()

    setErrorUploadFile('')
    dispatch(
      setMassivePaymentExcelValidation({
        data: null,
        isLoading: false,
        errors: [],
      })
    )
    dispatch(
      setMassivePaymentExcelPayment({
        data: null,
        isLoading: false,
        error: null,
      })
    )

    const file = e.target.files[0]

    if (!validateExcelFile(file)) {
      setErrorUploadFile('Only .xlsx and .xls files are allowed')
      return
    }

    setInputFile(file)
  }

  function handleValidateFile() {
    const userId = user?.usuarioid
    if (!userId) return
    dispatch(validateExcelMassivePayment(inputFile, userId))
  }

  function handleViewDetails() {
    setShowDetails((prev) => !prev)
  }

  function handleViewDetailsMassivePayments() {
    setShowDetailsMassivePayments((prev) => !prev)
  }

  function handleConfirmPayment() {
    const userId = user?.usuarioid
    if (!userId || !dataMassivePaymentsExcelValidation?.pagoMasivoId) return
    dispatch(
      createExcelMassivePayment(
        userId,
        dataMassivePaymentsExcelValidation?.pagoMasivoId
      )
    )
  }

  function handleCancelPayment() {
    dispatch(
      setMassivePaymentExcelValidation({
        data: null,
        isLoading: false,
        errors: [],
      })
    )
    dispatch(
      setMassivePaymentExcelPayment({
        data: null,
        isLoading: false,
        error: null,
      })
    )
  }

  function handleNavigateToMultiplePaymentsDetail(multiplePaymentsId) {
    navigate(`/home/multiple-payments/${multiplePaymentsId}`)
  }

  function handleDownloadExcel(paymentId) {
    const paymentToDownload = dataMassivePaymentsExcelStatus?.data?.filter(
      (payment) => payment?.id === paymentId
    )?.[0]

    if (!paymentToDownload) return

    // Crear data con solo el campo ID
    const excelData = paymentToDownload?.pagos?.map((pago) => ({
      ID: pago.id,
      'Monto a pagar': pago.monto_pagar,
      'Monto a recibir': pago.monto_recibir,
      'Forma de pago': pago.forma_pago,
      'Numero de cuenta': pago.numero_cuenta,
      RED: pago.red,
      'Moneda que recibe': pago.moneda_recibe,
      Balance: pago.balance,
      Colaborador: pago.colaborador,
      Mail: pago.mail,
      'ID de pago': pago.id_pago,
      'Transaccion/concepto': pago.transaccion_concepto,
      Opcional: pago.opcional,
      'Fecha de pago': pago.fecha_pago,
    }))

    // Crear worksheet desde JSON
    const worksheet = XLSX.utils.json_to_sheet(excelData)

    // Crear workbook y agregar la hoja
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pagos')

    // Generar el archivo como buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    })

    // Crear Blob y descargar
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    saveAs(blob, `pagos-${paymentId}.xlsx`)
  }

  useEffect(() => {
    if (user?.usuarioid) {
      dispatch(getExcelMassivePaymentsStatus(user?.usuarioid))
    }
  }, [dispatch, user?.usuarioid])

  if (isLoadingMassivePaymentsExcelStatus) {
    return <Loader />
  }

  if (
    dataMassivePaymentsExcelStatus?.data?.[0]?.estado ===
      StatesMassivePayments.PROCESSING &&
    dataMassivePaymentsExcelStatus?.pagination?.page === 1
  ) {
    return (
      <Announcement
        description={
          <>
            <NormalText>
              The last massive payment it's currently being processed. You need
              to wait for it to be finished.
            </NormalText>
          </>
        }
      />
    )
  }

  return (
    <>
      <UploadFile
        handleAddFile={handleAddFile}
        handleRemoveFile={() => setInputFile(null)}
        label={'Upload Excel'}
        file={inputFile}
        id={null}
        isLoadingFile={false}
        message={errorUploadFile}
      />
      {/* Error validation excel */}
      {errorsMassivePaymentsExcelValidation.length > 0 && (
        <Announcement
          title={'Error'}
          description={
            <>
              <NormalText isBold={'bold'}>
                You need to fix this errors to continue with the process.
              </NormalText>
              {errorsMassivePaymentsExcelValidation?.map((error) => (
                <Box
                  key={error?.error}
                  sx={{
                    border: '1px solid #D9D9D9',
                    padding: '8px',
                    borderRadius: '4px',
                    marginTop: '4px',
                  }}
                >
                  <NormalText>Field: {error?.campo}</NormalText>
                  <NormalText>Row: {error?.fila}</NormalText>
                  <NormalText>Error: {error?.error}</NormalText>
                </Box>
              ))}
            </>
          }
          type={'error'}
        />
      )}
      {/* Success validation excel */}
      {dataMassivePaymentsExcelValidation?.data?.length > 0 && (
        <Announcement
          description={
            <Box
              display={'flex'}
              flexDirection={'column'}
              gap={'8px'}
              alignItems={'center'}
              width={'100%'}
            >
              <NormalText isBold color={'#0F110C'}>
                {dataMassivePaymentsExcelValidation?.data?.length} transactions
                processed successfully.
              </NormalText>
              <NormalText>
                You can now make the payments with the transactions processed.
              </NormalText>
              <Button
                startIcon={showDetails ? 'EyeSlash' : 'Eye'}
                label={showDetails ? 'Hide details' : 'View details'}
                onClick={handleViewDetails}
                type="regular"
                buttonStyles={{
                  fontSize: '10px !important',
                  marginTop: '8px',
                  gap: '4px !important',
                }}
              />
              {showDetails && (
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  gap={'8px'}
                  mt={'8px'}
                >
                  <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                    <NormalText isBold>Total amount:</NormalText>
                    <Box display={'flex'} alignItems={'center'} gap={'2px'}>
                      <img
                        src={mockedTokensIcons?.[1]?.logo}
                        alt={'USDT icon'}
                        style={{ width: '24px', height: '24px' }}
                      />
                      <NormalText>
                        {dataMassivePaymentsExcelValidation?.totalMontoAPagar}
                      </NormalText>
                    </Box>
                  </Box>
                  {dataMassivePaymentsExcelValidation?.data?.map(
                    (transaction) => (
                      <Box
                        key={transaction?.ID}
                        sx={{
                          borderBottom: '1px solid #D9D9D9',
                          padding: '8px 4px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                          <NormalText isBold>ID:</NormalText>
                          <NormalText>{transaction?.id_pago}</NormalText>
                        </Box>
                        <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                          <NormalText isBold>Contributor:</NormalText>
                          <NormalText>{transaction?.colaborador}</NormalText>
                        </Box>
                        <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                          <NormalText isBold>Mail:</NormalText>
                          <NormalText>{transaction?.mail}</NormalText>
                        </Box>
                        <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                          <NormalText isBold>Network:</NormalText>
                          <NormalText>{transaction?.red}</NormalText>
                        </Box>
                        <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                          <NormalText isBold>Transaction concept:</NormalText>
                          <NormalText>
                            {transaction?.transaccion_concepto}
                          </NormalText>
                        </Box>
                        <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                          <NormalText isBold>Wallet:</NormalText>
                          <NormalText>{transaction?.numero_cuenta}</NormalText>
                        </Box>
                        <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                          <NormalText isBold>Payment method:</NormalText>
                          <NormalText>{transaction?.forma_pago}</NormalText>
                        </Box>
                      </Box>
                    )
                  )}
                </Box>
              )}
              {!!errorMassivePaymentsExcelPayment && (
                <Announcement
                  title={'Error'}
                  description={
                    <NormalText>{errorMassivePaymentsExcelPayment}</NormalText>
                  }
                  type={'error'}
                />
              )}
              {dataMassivePaymentsExcelPayment && (
                <Announcement
                  title={'Success'}
                  description={
                    <NormalText>{dataMassivePaymentsExcelPayment}</NormalText>
                  }
                />
              )}
              {!dataMassivePaymentsExcelPayment && (
                <Box
                  display={'flex'}
                  gap={'8px'}
                  mt={'8px'}
                  justifyContent={'flex-end'}
                >
                  <Button
                    label="Cancel"
                    onClick={handleCancelPayment}
                    isDisabled={
                      dataMassivePaymentsExcelValidation?.data?.length === 0
                    }
                    buttonStyles={{
                      fontSize: '10px !important',
                    }}
                  />
                  <Button
                    label="Confirm"
                    onClick={handleConfirmPayment}
                    isDisabled={
                      dataMassivePaymentsExcelValidation?.data?.length === 0
                    }
                    buttonStyles={{
                      fontSize: '10px !important',
                    }}
                    isLoading={isLoadingMassivePaymentsExcelPayment}
                  />
                </Box>
              )}
            </Box>
          }
        />
      )}
      {!dataMassivePaymentsExcelValidation?.data?.length && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Button
            label="Validate file"
            onClick={handleValidateFile}
            isDisabled={inputFile === null}
            isLoading={isLoadingMassivePaymentsExcelValidation}
          />
        </Box>
      )}
      {!errorMassivePaymentsExcelStatus && (
        <Announcement
          description={
            <>
              <NormalText color={palette.primary[1]}>
                The last massive payment was processed successfully.
              </NormalText>
              <Button
                label={
                  showDetailsMassivePayments ? 'Hide details' : 'View details'
                }
                startIcon={showDetailsMassivePayments ? 'EyeSlash' : 'Eye'}
                onClick={handleViewDetailsMassivePayments}
                type="regular"
                buttonStyles={{
                  fontSize: '10px !important',
                }}
              />
              {showDetailsMassivePayments && (
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  gap={'8px'}
                  alignItems={'center'}
                >
                  {dataMassivePaymentsExcelStatus?.data?.map((payment) => (
                    <Box
                      key={payment?.ID}
                      sx={{
                        border: '1px solid #D9D9D9',
                        padding: '8px',
                        borderRadius: '4px',
                        marginTop: '4px',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        position: 'relative',
                      }}
                    >
                      <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                        <NormalText isBold>ID:</NormalText>
                        <NormalText>{payment?.id}</NormalText>
                      </Box>
                      <Box
                        display={'flex'}
                        gap={'4px'}
                        alignItems={'center'}
                        sx={{
                          position: 'absolute',
                          right: '8px',
                          top: '8px',
                        }}
                      >
                        <Tag
                          label={
                            payment?.estado?.slice(0, 1).toUpperCase() +
                            payment?.estado?.slice(1)?.toLowerCase()
                          }
                          type={getTagType(payment?.estado)}
                        />
                      </Box>
                      <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                        <NormalText isBold>Date:</NormalText>
                        <NormalText>
                          {formatDate(payment?.fecha_subida)}
                        </NormalText>
                      </Box>
                      <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                        <NormalText isBold>File name:</NormalText>
                        <NormalText>{payment?.nombre_archivo}</NormalText>
                      </Box>
                      <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                        <NormalText isBold>Total amount:</NormalText>
                        <Box display={'flex'} alignItems={'center'} gap={'2px'}>
                          <img
                            src={mockedTokensIcons?.[1]?.logo}
                            alt={'USDT icon'}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <NormalText>{payment?.total_monto}</NormalText>
                        </Box>
                      </Box>
                      <Box display={'flex'} gap={'4px'} alignItems={'center'}>
                        <NormalText isBold>Total payments:</NormalText>
                        <NormalText>{payment?.total_pagos}</NormalText>
                      </Box>
                      <Box display={'flex'} gap={'8px'} alignItems={'center'}>
                        <Button
                          label="View details"
                          onClick={() =>
                            handleNavigateToMultiplePaymentsDetail(payment?.id)
                          }
                          type={'regular'}
                          startIcon={'Eye'}
                          buttonStyles={{
                            fontSize: '10px !important',
                            marginTop: '8px',
                            gap: '4px !important',
                          }}
                        />
                        <Button
                          label="Download Excel"
                          onClick={() => handleDownloadExcel(payment?.id)}
                          type={'regular'}
                          startIcon={'Download'}
                          buttonStyles={{
                            fontSize: '10px !important',
                            marginTop: '8px',
                            gap: '4px !important',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                  <Pagination
                    count={Math.ceil(
                      dataMassivePaymentsExcelStatus?.pagination?.totalObjects /
                        dataMassivePaymentsExcelStatus?.pagination?.elements
                    )}
                    page={dataMassivePaymentsExcelStatus?.pagination?.page}
                    onChange={(_, value) => {
                      return dispatch(
                        getExcelMassivePaymentsStatus(user?.usuarioid, value)
                      )
                    }}
                    size="large"
                    sx={{ alignSelf: 'center', marginTop: '8px' }}
                  />
                </Box>
              )}
            </>
          }
        />
      )}
    </>
  )
}
